import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Eye, Globe, Award, Users, Sparkles } from 'lucide-react';
import { Layout } from '../components/layout/Layout';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } }
};

export default function AboutPage() {
  const { t } = useTranslation();

  const values = [
    { icon: Eye, title: 'Visión Creativa', titleEn: 'Creative Vision', desc: 'Cada proyecto es una oportunidad para innovar', descEn: 'Every project is an opportunity to innovate' },
    { icon: Target, title: 'Mentalidad Empresarial', titleEn: 'Business Mindset', desc: 'Arte con visión de negocio', descEn: 'Art with business vision' },
    { icon: Globe, title: 'Red Internacional', titleEn: 'International Network', desc: 'Conexiones globales, impacto local', descEn: 'Global connections, local impact' },
    { icon: Award, title: 'Excelencia', titleEn: 'Excellence', desc: 'Calidad en cada detalle', descEn: 'Quality in every detail' },
  ];

  const stats = [
    { number: '150+', label: 'Eventos Producidos', labelEn: 'Events Produced' },
    { number: '80+', label: 'Artistas Representados', labelEn: 'Artists Represented' },
    { number: '50+', label: 'Venues Aliados', labelEn: 'Partner Venues' },
    { number: '12', label: 'Países', labelEn: 'Countries' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center pt-32 pb-20" data-testid="about-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>

        <div className="container-premium relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <img 
                src="/gs-agency-logo-gold.png" 
                alt="GS Agency" 
                className="h-48 md:h-64 w-auto object-contain"
              />
            </motion.div>
            <motion.p variants={fadeInUp} className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-6">
              {t('about.title')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl text-white mb-8">
              {t('about.subtitle')}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-white/60 text-xl leading-relaxed">
              {t('about.description')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-subtle py-24" data-testid="values-section">
        <div className="container-premium">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="text-center"
                >
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                    <value.icon size={28} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="font-display text-xl text-white mb-3">{value.title}</h3>
                  <p className="text-white/50 text-sm">{value.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-dark py-24" data-testid="mission-section">
        <div className="container-premium">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="premium-card p-10"
            >
              <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                <Eye size={28} className="text-[#D4AF37]" />
              </div>
              <h3 className="font-display text-3xl text-white mb-4">{t('about.vision')}</h3>
              <p className="text-white/60 leading-relaxed">
                {t('about.vision_text')}
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="premium-card p-10"
            >
              <div className="w-14 h-14 rounded-xl bg-[#C0C0C0]/10 flex items-center justify-center mb-6">
                <Target size={28} className="text-[#C0C0C0]" />
              </div>
              <h3 className="font-display text-3xl text-white mb-4">{t('about.mission')}</h3>
              <p className="text-white/60 leading-relaxed">
                {t('about.mission_text')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 relative overflow-hidden" data-testid="stats-section">
        <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/5 to-transparent" />
        <div className="container-premium relative">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center"
              >
                <p className="font-display text-5xl md:text-6xl text-gold-gradient mb-2">
                  {stat.number}
                </p>
                <p className="text-white/50 text-sm tracking-wide uppercase">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="section-subtle py-24" data-testid="why-us-section">
        <div className="container-premium">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="text-center mb-16">
              <h2 className="font-display text-4xl md:text-5xl text-white">
                ¿Por Qué <span className="text-gold-gradient">GS Agency</span>?
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div variants={fadeInUp} className="premium-card p-8">
                <Sparkles className="text-[#D4AF37] mb-4" size={32} />
                <h4 className="font-display text-xl text-white mb-3">Visión Creativa + Negocio</h4>
                <p className="text-white/50 text-sm leading-relaxed">
                  No solo vendemos shows. Creamos experiencias que posicionan marcas y talento en el mercado.
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="premium-card p-8">
                <Globe className="text-[#C0C0C0] mb-4" size={32} />
                <h4 className="font-display text-xl text-white mb-3">Red Internacional</h4>
                <p className="text-white/50 text-sm leading-relaxed">
                  Conexiones con artistas y colaboradores en más de 12 países. Perspectiva multicultural Brasil-México.
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="premium-card p-8">
                <Users className="text-[#D4AF37] mb-4" size={32} />
                <h4 className="font-display text-xl text-white mb-3">Impacto Emocional y Visual</h4>
                <p className="text-white/50 text-sm leading-relaxed">
                  Producciones con alto impacto emocional y visual que dejan huella en cada audiencia.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 section-dark" data-testid="about-cta">
        <div className="container-premium text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl text-white mb-6">
              ¿Listo para crear algo <span className="text-gold-gradient italic">extraordinario</span>?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
              Trabajemos juntos en tu próximo proyecto. Desde booking artístico hasta producción de eventos completa.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/contact" className="btn-gold inline-flex items-center gap-2">
                {t('hero.cta_contact')}
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
